import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox';
import { CheckIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * A searchable, optionally multiple-selection control.
 *
 * Thin wrappers over `@base-ui/react`'s combobox, in the same shape as
 * `dropdown-menu.tsx`: named parts, a `data-slot` on each, and `cn()` so a
 * caller can extend without replacing. Nothing here decides behaviour — the
 * feature that uses it owns what the options are, what selecting one means, and
 * whether a value that matches nothing offers to become one.
 *
 * `dropdown-menu` covers the "pick one of a few known actions" case and stays
 * the right choice for that. This exists for the case it cannot serve: a list
 * long enough to need typing, and a selection that can hold more than one.
 */
function Combobox<Value, Multiple extends boolean | undefined = false>(
  props: ComboboxPrimitive.Root.Props<Value, Multiple>,
) {
  // Generic rather than pinned: `Multiple` decides whether `value` is one item
  // or a list, so fixing it here would make the wrapper usable for only half of
  // what the primitive does — and the label picker needs the multiple half.
  return <ComboboxPrimitive.Root data-slot="combobox" {...props} />;
}

/** Wraps the input and any selected chips so they read as one field. */
function ComboboxInputGroup({
  className,
  ...props
}: ComboboxPrimitive.InputGroup.Props) {
  return (
    <ComboboxPrimitive.InputGroup
      data-slot="combobox-input-group"
      className={cn(
        // Drawn like `Input`: the page surface with the input border. Filled with
        // `bg-input` it read as a disabled control in light mode, where that
        // token is the same grey as the panel behind it.
        'flex w-full shrink-0 flex-wrap items-center gap-1 rounded-md border border-input bg-transparent px-2 py-1.5 text-sm focus-within:ring-3 focus-within:ring-ring focus-within:outline-none dark:bg-input/30',
        className,
      )}
      {...props}
    />
  );
}

function ComboboxInput({ className, ...props }: ComboboxPrimitive.Input.Props) {
  return (
    <ComboboxPrimitive.Input
      data-slot="combobox-input"
      className={cn(
        'min-w-24 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground pointer-coarse:-my-1.5 pointer-coarse:min-h-11',
        className,
      )}
      {...props}
    />
  );
}

function ComboboxChips({ className, ...props }: ComboboxPrimitive.Chips.Props) {
  return (
    <ComboboxPrimitive.Chips
      data-slot="combobox-chips"
      className={cn('contents', className)}
      {...props}
    />
  );
}

function ComboboxChip({ className, ...props }: ComboboxPrimitive.Chip.Props) {
  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      className={cn('inline-flex items-center gap-1', className)}
      {...props}
    />
  );
}

function ComboboxChipRemove({
  className,
  ...props
}: ComboboxPrimitive.ChipRemove.Props) {
  return (
    <ComboboxPrimitive.ChipRemove
      data-slot="combobox-chip-remove"
      className={cn(
        'rounded-full opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
        className,
      )}
      {...props}
    />
  );
}

function ComboboxTrigger({ ...props }: ComboboxPrimitive.Trigger.Props) {
  return <ComboboxPrimitive.Trigger data-slot="combobox-trigger" {...props} />;
}

/** Portal → Positioner → Popup, matching `DropdownMenuContent`. */
function ComboboxContent({
  align = 'start',
  side = 'bottom',
  sideOffset = 4,
  className,
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<ComboboxPrimitive.Positioner.Props, 'align' | 'side' | 'sideOffset'>) {
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        className="isolate z-50 outline-none"
        align={align}
        side={side}
        sideOffset={sideOffset}
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          className={cn(
            'z-50 flex max-h-(--available-height) w-(--anchor-width) max-w-(--available-width) origin-(--transform-origin) flex-col overflow-hidden rounded-lg bg-popover p-1 text-popover-foreground shadow-raised ring-1 ring-foreground/10 duration-[var(--motion-duration-base)] ease-[var(--motion-ease-standard)] data-closed:duration-[var(--motion-duration-fast)] data-closed:ease-[var(--motion-ease-exit)] outline-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
            className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
}

/**
 * The options, bounded and scrolling.
 *
 * The popup used to grow with its contents up to `--available-height`, which on
 * a tall window is most of the viewport — a workspace with thirty labels opened
 * a list that ran nearly the height of the screen. Worse, the scroll lived on
 * the *content*, so the search field scrolled away with the options: the one
 * control that makes a long list usable disappeared exactly when the list got
 * long enough to need it.
 *
 * The bound is here instead, on the list alone. The field above it and the
 * create row and the way through to settings below it stay put, and only the
 * options move. `--available-height` stays on the content as the outer limit,
 * for a window too short even for this.
 */
function ComboboxList({ className, ...props }: ComboboxPrimitive.List.Props) {
  return (
    <ComboboxPrimitive.List
      data-slot="combobox-list"
      className={cn('max-h-64 min-h-0 overflow-y-auto', className)}
      {...props}
    />
  );
}

/** Groups related items under one heading — e.g. a model picker's tiers. */
function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
  return (
    <ComboboxPrimitive.Group
      data-slot="combobox-group"
      className={cn('flex flex-col', className)}
      {...props}
    />
  );
}

/** A group's heading — automatically associated with its parent group. */
function ComboboxGroupLabel({
  className,
  ...props
}: ComboboxPrimitive.GroupLabel.Props) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-group-label"
      className={cn(
        'flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

function ComboboxItem({
  className,
  children,
  ...props
}: ComboboxPrimitive.Item.Props) {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      className={cn(
        'flex cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground',
        className,
      )}
      {...props}
    >
      <ComboboxPrimitive.ItemIndicator className="flex size-4 shrink-0 items-center justify-center">
        <CheckIcon className="size-3.5" />
      </ComboboxPrimitive.ItemIndicator>
      {children}
    </ComboboxPrimitive.Item>
  );
}

/**
 * Shown when nothing matches what was typed.
 *
 * Deliberately a slot rather than a fixed message: for the label picker this is
 * where "Create `<name>`" lives, which is the difference between a vocabulary
 * that gets filled and one that stays empty.
 */
function ComboboxEmpty({ className, ...props }: ComboboxPrimitive.Empty.Props) {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={cn('px-2 py-1.5 text-sm', className)}
      {...props}
    />
  );
}

export {
  Combobox,
  ComboboxChip,
  ComboboxChipRemove,
  ComboboxChips,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxInput,
  ComboboxInputGroup,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
};
